const safeGet = (obj, path) => {
  if (!path) return '';
  const val = obj[path];
  if (typeof val === 'string') return val.trim();
  return val === undefined || val === null ? '' : val;
};

const FUNCTIONS = {
  selected: (val, target) => {
    const t = String(target);
    if (Array.isArray(val)) return val.map(String).includes(t);
    const v = String(val || '');
    return v.split(' ').includes(t) || v === t;
  },
  countSelected: val => {
    if (Array.isArray(val)) return val.length;
    if (!val) return 0;
    const v = String(val).trim();
    return v === '' ? 0 : v.split(' ').length;
  },
  count: val => {
    if (Array.isArray(val)) return val.length;
    return val ? 1 : 0;
  },
  sum: arr => {
    if (Array.isArray(arr)) return arr.reduce((a, b) => a + (Number(b) || 0), 0);
    return Number(arr) || 0;
  },
  avg: arr => {
    if (Array.isArray(arr)) return arr.length > 0 ? FUNCTIONS.sum(arr) / arr.length : 0;
    return Number(arr) || 0;
  },
  min: arr => {
    if (Array.isArray(arr)) return arr.length > 0 ? Math.min(...arr.map(v => Number(v) || 0)) : 0;
    return Number(arr) || 0;
  },
  max: arr => {
    if (Array.isArray(arr)) return arr.length > 0 ? Math.max(...arr.map(v => Number(v) || 0)) : 0;
    return Number(arr) || 0;
  },
  indexedRepeat: (arr, idx) => {
    if (Array.isArray(arr)) return arr[(Number(idx) || 1) - 1] || '';
    return arr || '';
  },
  coalesce: (...args) => args.find(a => a !== null && a !== '' && a !== undefined) || '',
  ifFn: (cond, t, f) => (cond ? t : f),
  empty: v => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    return false;
  },
  notFn: v => !v,
  concat: (...args) => args.map(a => (a === null || a === undefined ? '' : String(a))).join(''),
  regex: (v, p) => {
    try {
      return new RegExp(p).test(String(v));
    } catch {
      return false;
    }
  },
  substr: (v, s, l) => String(v).substr(s, l),
  stringLength: v => String(v).length,
  upper: v => String(v).toUpperCase(),
  lower: v => String(v).toLowerCase(),
  round: (v, p) => {
    const num = Number(v);
    if (isNaN(num)) return '';
    const f = Math.pow(10, p || 0);
    return Math.round(num * f) / f;
  },
  int: v => parseInt(v, 10) || 0,
  pow: (b, e) => Math.pow(Number(b) || 0, Number(e) || 0),
  abs: v => Math.abs(Number(v) || 0),
  sqrt: v => Math.sqrt(Number(v) || 0),
  log: v => Math.log(Number(v) || 0),
  today: () => new Date().toISOString().split('T')[0],
  now: () => new Date().toISOString(),
  random: () => Math.random(),
  uuid: (l = 36) => {
    const c = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let r = '';
    for (let i = 0; i < l; i++) r += c.charAt(Math.floor(Math.random() * c.length));
    return r;
  },
  once: v => v,
  formatDateTime: (d, f) => {
    try {
      const date = new Date(d);
      if (isNaN(date.getTime())) return '';
      return f
        .replace('%Y', date.getFullYear())
        .replace('%m', String(date.getMonth() + 1).padStart(2, '0'))
        .replace('%d', String(date.getDate()).padStart(2, '0'));
    } catch {
      return '';
    }
  },
  index: () => 1,
  position: (l = 0) => l,
  relevant: v => v !== undefined && v !== null && v !== '',
  decimalTime: v => {
    if (!v) return 0;
    const parts = String(v).split(':');
    if (parts.length < 2) return 0;
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parts[2] ? parseInt(parts[2], 10) || 0 : 0;
    return (hours * 3600 + minutes * 60 + seconds) / 86400;
  },
  decimalDate: v => {
    if (!v) return 0;
    const date = new Date(v);
    if (isNaN(date.getTime())) return 0;
    return date.getTime() / 86400000;
  },
  decimalDateTime: v => {
    if (!v) return 0;
    const date = new Date(v);
    if (isNaN(date.getTime())) return 0;
    return date.getTime() / 86400000;
  },
  pi: () => Math.PI,
  floor: v => Math.floor(Number(v) || 0),
  ceiling: v => Math.ceil(Number(v) || 0),
  exp: v => Math.exp(Number(v) || 0),
  exp10: v => Math.pow(10, Number(v) || 0),
  log10: v => Math.log10(Number(v) || 0),
  sin: v => Math.sin(Number(v) || 0),
  cos: v => Math.cos(Number(v) || 0),
  tan: v => Math.tan(Number(v) || 0),
  asin: v => Math.asin(Number(v) || 0),
  acos: v => Math.acos(Number(v) || 0),
  atan: v => Math.atan(Number(v) || 0),
  atan2: (y, x) => Math.atan2(Number(y) || 0, Number(x) || 0),
  trueFn: () => true,
  falseFn: () => false,
  boolean: v => {
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
    return !!v;
  },
  booleanFromString: v => v === 'true' || v === '1',
  number: v => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  },
  string: v => (v === null || v === undefined ? '' : String(v)),
  selectedAt: (val, idx) => {
    const list = String(val || '').split(' ');
    const i = parseInt(idx, 10) || 0;
    return list[i] || '';
  },
  join: (sep, val) => (Array.isArray(val) ? val.join(String(sep || ' ')) : String(val))
};

export const evaluateExpression = (expression, values, pathPrefix = '', currFieldName = '') => {
  if (!expression || String(expression).trim() === '') return true;
  if (expression === '1=1') return true;

  try {
    let expr = expression.toString();

    if (currFieldName) {
      expr = expr.replace(/\B\.\B/g, `\${${currFieldName}}`);
    }

    expr = expr.replace(/\${([^}]+)}/g, (_, key) => {
      let val = '';
      if (pathPrefix && Object.prototype.hasOwnProperty.call(values, pathPrefix + key)) {
        val = safeGet(values, pathPrefix + key);
      } else if (Object.prototype.hasOwnProperty.call(values, key)) {
        val = safeGet(values, key);
      } else if (key.includes('/')) {
        const [group, field] = key.split('/');
        const matches = Object.keys(values)
          .filter(k => k.startsWith(`${group}_`) && k.endsWith(`_${field}`))
          .sort((a, b) => {
            const idxA = parseInt(a.replace(`${group}_`, '').split('_')[0]);
            const idxB = parseInt(b.replace(`${group}_`, '').split('_')[0]);
            return idxA - idxB;
          });
        if (matches.length > 0) {
          const arr = matches.map(m => safeGet(values, m));
          return JSON.stringify(arr);
        }
        return '[]';
      }

      if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val)))) {
        return Number(val);
      }
      if (typeof val === 'string') {
        const escaped = val.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        return `'${escaped}'`;
      }
      if (Array.isArray(val)) return JSON.stringify(val);
      if (val === '' || val === null || val === undefined) return "''";
      return val;
    });

    const funcMap = {
      not: 'notFn',
      empty: 'empty',
      selected: 'selected',
      'count-selected': 'countSelected',
      'string-length': 'stringLength',
      'format-date-time': 'formatDateTime',
      'indexed-repeat': 'indexedRepeat',
      count: 'count',
      sum: 'sum',
      avg: 'avg',
      min: 'min',
      max: 'max',
      coalesce: 'coalesce',
      concat: 'concat',
      if: 'ifFn',
      once: 'once',
      uuid: 'uuid',
      index: 'index',
      position: 'position',
      relevant: 'relevant',
      regex: 'regex',
      substr: 'substr',
      today: 'today',
      now: 'now',
      random: 'random',
      upper: 'upper',
      lower: 'lower',
      round: 'round',
      int: 'int',
      pow: 'pow',
      abs: 'abs',
      sqrt: 'sqrt',
      log: 'log',
      'decimal-time': 'decimalTime',
      'decimal-date': 'decimalDate',
      'decimal-date-time': 'decimalDateTime',
      pi: 'pi',
      floor: 'floor',
      ceiling: 'ceiling',
      exp: 'exp',
      exp10: 'exp10',
      log10: 'log10',
      sin: 'sin',
      cos: 'cos',
      tan: 'tan',
      asin: 'asin',
      acos: 'acos',
      atan: 'atan',
      atan2: 'atan2',
      true: 'trueFn',
      false: 'falseFn',
      boolean: 'boolean',
      'boolean-from-string': 'booleanFromString',
      number: 'number',
      string: 'string',
      'selected-at': 'selectedAt',
      join: 'join'
    };

    Object.entries(funcMap).forEach(([oldName, newName]) => {
      const regex = new RegExp(`\\b${oldName}\\s*\\(`, 'gi');
      expr = expr.replace(regex, `FUNCTIONS.${newName}(`);
    });

    expr = expr.replace(/\bplus\b/gi, ' + ');
    expr = expr.replace(/\bminus\b/gi, ' - ');
    expr = expr.replace(/\band\b/gi, ' && ');
    expr = expr.replace(/\bor\b/gi, ' || ');
    expr = expr.replace(/\bdiv\b/gi, ' / ');
    expr = expr.replace(/\bmod\b/gi, ' % ');
    expr = expr.replace(/([^=<>!])=([^=])/g, '$1==$2');
    expr = expr.replace(/''\s*==\s*''/g, 'true');

    if (pathPrefix && pathPrefix.includes('_')) {
      const match = pathPrefix.match(/_(\d+)_$/);
      if (match) {
        const idx = parseInt(match[1]) + 1;
        expr = expr.replace(/FUNCTIONS\.index\(\)/g, String(idx));
        expr = expr.replace(/FUNCTIONS\.position\(\)/g, String(idx));
      }
    }

    // ── Bare field-name substitution ────────────────────────────────────────
    // Formulas may reference fields as bare identifiers (e.g. `field_name_abc`)
    // without ${} wrappers. Replace each known key from `values` with its
    // literal value so `new Function()` can evaluate them correctly.
    // We sort by descending length to prevent shorter names from matching
    // inside longer ones (e.g. "score" inside "total_score").
    const RESERVED = new Set([
      'FUNCTIONS','true','false','null','undefined','NaN','Infinity',
      'return','var','let','const','if','else','typeof','instanceof',
    ]);
    const allValues = { ...values };
    const sortedKeys = Object.keys(allValues).sort((a, b) => b.length - a.length);
    for (const key of sortedKeys) {
      if (RESERVED.has(key)) continue;
      // Only replace if the bare name actually appears in the expression
      // (word-boundary match, not preceded by '.', '$', '{' or '"/')
      const bareRe = new RegExp(`(?<![.${'{'}"'/])\\b${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b(?![}'"(])`, 'g');
      if (!bareRe.test(expr)) continue;
      bareRe.lastIndex = 0; // reset after .test()

      const rawVal = pathPrefix && Object.prototype.hasOwnProperty.call(allValues, pathPrefix + key)
        ? allValues[pathPrefix + key]
        : allValues[key];

      let replacement;
      if (rawVal === null || rawVal === undefined || rawVal === '') {
        replacement = '0'; // treat empty as 0 for arithmetic
      } else if (typeof rawVal === 'boolean') {
        replacement = String(rawVal);
      } else if (typeof rawVal === 'number' || (typeof rawVal === 'string' && rawVal.trim() !== '' && !isNaN(Number(rawVal)))) {
        replacement = String(Number(rawVal));
      } else if (Array.isArray(rawVal)) {
        replacement = JSON.stringify(rawVal);
      } else {
        const escaped = String(rawVal).replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        replacement = `'${escaped}'`;
      }
      expr = expr.replace(bareRe, replacement);
    }
    // ────────────────────────────────────────────────────────────────────────

    const func = new Function('FUNCTIONS', `return (${expr})`);
    const res = func(FUNCTIONS);
    return typeof res === 'number' && !isFinite(res) ? '' : res;
  } catch {
    return '';
  }
};

export const processFormCalculations = (formObj, values, activeRepeats = {}) => {
  const updates = {};
  const traverse = (sections, pathPrefix = '') => {
    sections?.forEach(sec => {
      if (sec.is_repeat) {
        const key = pathPrefix ? `${pathPrefix}${sec.name}` : sec.name;
        const count = activeRepeats[key] || 0;
        for (let i = 0; i < count; i++) {
          const itemPath = `${pathPrefix}${sec.name}_${i}_`;
          sec.fields?.forEach(f => {
            const formula = f.calculation || f.calculate;
            if (formula) {
              try {
                const name = itemPath + f.name;
                const neu = evaluateExpression(formula, { ...values, ...updates }, itemPath, f.name);
                if (values[name] !== neu && typeof neu !== 'boolean') updates[name] = neu;
              } catch {
                /* */
              }
            }
          });
          if (sec.sections) traverse(sec.sections, itemPath);
        }
      } else {
        sec.fields?.forEach(f => {
          const formula = f.calculation || f.calculate;
          if (formula) {
            try {
              const name = pathPrefix + f.name;
              const neu = evaluateExpression(formula, { ...values, ...updates }, pathPrefix, f.name);
              if (values[name] !== neu && typeof neu !== 'boolean') updates[name] = neu;
            } catch {
              /* */
            }
          }
        });
        if (sec.sections) traverse(sec.sections, pathPrefix);
      }
    });
  };
  traverse(formObj.sections);

  // Also catch calculations for fields that might be directly in formObj.questions
  if (formObj.questions) {
    formObj.questions.forEach(f => {
      const formula = f.calculation || f.calculate;
      if (formula) {
        try {
          const neu = evaluateExpression(formula, { ...values, ...updates }, '', f.name);
          if (values[f.name] !== neu && typeof neu !== 'boolean') updates[f.name] = neu;
        } catch {
          /* */
        }
      }
    });
  }
  return updates;
};

export const checkRelevance = (item, values, pathPrefix = '') => {
  if (!item.relevance || item.relevance.trim() === '') return true;
  let rel = item.relevance.toString();
  if (rel.includes('deviceid')) {
    rel = rel.replace(/\${deviceid}\s*!=\s*['"]\(web\)['"]/gi, 'true').replace(/\${deviceid}\s*=\s*['"]\(web\)['"]/gi, 'true');
  }
  try {
    return !!evaluateExpression(rel, values, pathPrefix, item.name);
  } catch {
    return true;
  }
};

export const interpolateLabel = (text, values, pathPrefix = '', forUI = false) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(/\${([^}]+)}/g, (_, key) => {
    let val = '';
    if (pathPrefix && Object.prototype.hasOwnProperty.call(values, pathPrefix + key)) {
      val = safeGet(values, pathPrefix + key);
    } else {
      val = safeGet(values, key);
    }
    if (Array.isArray(val)) val = val.join(', ');
    const isEmpty = val === undefined || val === null || String(val).trim() === '';
    if (forUI) {
      const displayStr = isEmpty ? '-' : String(val);
      const colorClass = isEmpty ? 'bg-gray-50 text-gray-400 border-gray-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200 shadow-sm';
      return `<span class="inline-block px-1.5 py-px rounded border text-[13px] font-semibold tracking-tight mx-0.5 align-baseline transition-colors ${colorClass}">${displayStr}</span>`;
    }
    return !isEmpty ? String(val) : '';
  });
};
